/**
 * Assign Players Modal Component
 * Modale per l'assegnazione dei giocatori in Round Robin Manuale
 * Versione semplificata senza gestione gruppi e sostituzione massiva
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface Player {
  id: number;
  name: string;
  skill: string;
}

interface CurrentPlayers {
  side1_player1_id?: string;
  side1_player2_id?: string;
  side2_player1_id?: string;
  side2_player2_id?: string;
}

interface ValidationWarning {
  type: string;
  severity?: 'warning' | 'info' | 'error';
  message: string;
  icon?: string;
  player_id?: number;
  player_name?: string;
  times?: number;
  match_ids?: number[];
  side?: 'side1' | 'side2';
  player_ids?: number[];
}

interface PlayerSuggestion {
  player1_id: number;
  player1_name: string;
  player2_id: number;
  player2_name: string;
  reason: string;
}

@customElement('pm-assign-players-modal')
export class AssignPlayersModal extends LitElement {
  @property({ type: String, attribute: 'tournament-id' }) tournamentId = '';
  @property({ type: Array, attribute: false }) allPlayers: Player[] = [];

  @state() private isOpen = false;
  @state() private isLoading = false;
  @state() private currentMatchId: number | null = null;
  @state() private currentRoundNumber: number | null = null;
  @state() private selectedPlayers = {
    side1_player1: '',
    side1_player2: '',
    side2_player1: '',
    side2_player2: ''
  };
  @state() private isSaving = false;
  @state() private errorMessage = '';
  @state() private validationWarnings: ValidationWarning[] = [];
  @state() private playerSuggestions: PlayerSuggestion[] = [];
  @state() private availablePlayerIds: number[] = []; // IDs dei giocatori disponibili nella giornata

  static styles = css`
    :host {
      --modal-z-index: 1055;
      --backdrop-z-index: 1050;
    }

    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: var(--backdrop-z-index);
      display: none;
    }

    .modal-backdrop.show {
      display: block;
      animation: fadeIn 0.15s ease-in;
    }

    .modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: var(--modal-z-index);
      display: none;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .modal.show {
      display: block;
    }

    .modal-dialog {
      position: relative;
      width: auto;
      max-width: 800px;
      margin: 1.75rem auto;
      pointer-events: none;
    }

    .modal-content {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      pointer-events: auto;
      background-color: var(--bs-body-bg, #fff);
      background-clip: padding-box;
      border: 1px solid var(--bs-border-color, rgba(0,0,0,.2));
      border-radius: 0.5rem;
      outline: 0;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1rem;
      border-bottom: 1px solid var(--bs-border-color, #dee2e6);
      border-top-left-radius: calc(0.5rem - 1px);
      border-top-right-radius: calc(0.5rem - 1px);
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .modal-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.5;
    }

    .btn-close {
      box-sizing: content-box;
      width: 1em;
      height: 1em;
      padding: 0.25em 0.25em;
      color: white;
      background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
      border: 0;
      border-radius: 0.25rem;
      opacity: 0.8;
      cursor: pointer;
    }

    .btn-close:hover {
      opacity: 1;
    }

    .modal-body {
      position: relative;
      flex: 1 1 auto;
      padding: 1rem;
    }

    .modal-footer {
      display: flex;
      flex-wrap: wrap;
      flex-shrink: 0;
      align-items: center;
      justify-content: flex-end;
      padding: 0.75rem;
      border-top: 1px solid var(--bs-border-color, #dee2e6);
      border-bottom-right-radius: calc(0.5rem - 1px);
      border-bottom-left-radius: calc(0.5rem - 1px);
      gap: 0.5rem;
    }

    .alert {
      position: relative;
      padding: 0.75rem 1.25rem;
      margin-bottom: 1rem;
      border: 1px solid transparent;
      border-radius: 0.375rem;
    }

    .alert-info {
      color: #084298;
      background-color: #cfe2ff;
      border-color: #b6d4fe;
    }

    .alert-warning {
      color: #664d03;
      background-color: #fff3cd;
      border-color: #ffecb5;
    }

    .alert-danger {
      color: #842029;
      background-color: #f8d7da;
      border-color: #f5c2c7;
    }

    [data-bs-theme="dark"] .alert-info {
      color: #6ea8fe;
      background-color: #031633;
      border-color: #084298;
    }

    [data-bs-theme="dark"] .alert-warning {
      color: #ffda6a;
      background-color: #332701;
      border-color: #664d03;
    }

    [data-bs-theme="dark"] .alert-danger {
      color: #ea868f;
      background-color: #2c0b0e;
      border-color: #842029;
    }

    .card {
      position: relative;
      display: flex;
      flex-direction: column;
      min-width: 0;
      word-wrap: break-word;
      background-color: var(--bs-body-bg, #fff);
      background-clip: border-box;
      border: 1px solid var(--bs-border-color, rgba(0,0,0,.125));
      border-radius: 0.375rem;
      margin-bottom: 1rem;
    }

    .card-header {
      padding: 0.75rem 1.25rem;
      margin-bottom: 0;
      background-color: rgba(0,0,0,.03);
      border-bottom: 1px solid var(--bs-border-color, rgba(0,0,0,.125));
      border-top-left-radius: calc(0.375rem - 1px);
      border-top-right-radius: calc(0.375rem - 1px);
      font-weight: 600;
    }

    [data-bs-theme="dark"] .card-header {
      background-color: rgba(255,255,255,.05);
    }

    .card-body {
      flex: 1 1 auto;
      padding: 1.25rem 1.25rem;
    }

    .form-label {
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
    }

    .form-select {
      display: block;
      width: 100%;
      padding: 0.375rem 2.25rem 0.375rem 0.75rem;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      color: var(--bs-body-color, #212529);
      background-color: var(--bs-body-bg, #fff);
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      background-size: 16px 12px;
      border: 1px solid var(--bs-border-color, #ced4da);
      border-radius: 0.375rem;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      appearance: none;
    }

    .form-select:focus {
      border-color: #86b7fe;
      outline: 0;
      box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
    }

    .row {
      display: flex;
      flex-wrap: wrap;
      margin-right: -0.75rem;
      margin-left: -0.75rem;
    }

    .col-md-6 {
      flex: 0 0 auto;
      width: 50%;
      padding-right: 0.75rem;
      padding-left: 0.75rem;
    }

    .mb-3 {
      margin-bottom: 1rem !important;
    }

    .mb-0 {
      margin-bottom: 0 !important;
    }

    .mt-2 {
      margin-top: 0.5rem !important;
    }

    .me-2 {
      margin-right: 0.5rem !important;
    }

    .text-muted {
      color: #6c757d !important;
    }

    .text-danger {
      color: #dc3545 !important;
    }

    .btn {
      display: inline-block;
      font-weight: 400;
      line-height: 1.5;
      color: #212529;
      text-align: center;
      text-decoration: none;
      vertical-align: middle;
      cursor: pointer;
      user-select: none;
      background-color: transparent;
      border: 1px solid transparent;
      padding: 0.375rem 0.75rem;
      font-size: 1rem;
      border-radius: 0.375rem;
      transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }

    .btn:hover {
      color: #212529;
    }

    .btn:disabled {
      pointer-events: none;
      opacity: 0.65;
    }

    .btn-primary {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .btn-primary:hover {
      color: #fff;
      background-color: #0b5ed7;
      border-color: #0a58ca;
    }

    .btn-primary:disabled {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .btn-secondary {
      color: #fff;
      background-color: #6c757d;
      border-color: #6c757d;
    }

    .btn-secondary:hover {
      color: #fff;
      background-color: #5c636a;
      border-color: #565e64;
    }

    .icon {
      display: inline-block;
      width: 1em;
      height: 1em;
      vertical-align: -0.125em;
    }

    .spinner {
      animation: fa-spin 2s infinite linear;
    }

    @keyframes fa-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    small {
      font-size: 0.875rem;
    }

    ul {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    li {
      margin: 0.25rem 0;
    }

    strong {
      font-weight: 600;
    }

    /* Warnings e suggerimenti */
    .validation-warnings {
      margin: 1rem 0;
      padding: 0;
    }

    .warning-item {
      display: flex;
      align-items: start;
      padding: 0.75rem;
      margin-bottom: 0.5rem;
      border-radius: 0.375rem;
      font-size: 0.9rem;
    }

    .warning-item.warning {
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      color: #664d03;
    }

    .warning-item.info {
      background-color: #cfe2ff;
      border-left: 4px solid #0dcaf0;
      color: #084298;
    }

    .warning-item.error {
      background-color: #f8d7da;
      border-left: 4px solid #dc3545;
      color: #842029;
    }

    .warning-item.already_in_round {
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      color: #664d03;
    }

    .warning-item.already_teammates {
      background-color: #e7f3ff;
      border-left: 4px solid #0d6efd;
      color: #084298;
    }

    .warning-item.already_faced {
      background-color: #f8f9fa;
      border-left: 4px solid #6c757d;
      color: #495057;
    }

    .warning-icon {
      margin-right: 0.5rem;
      font-size: 1.2rem;
    }

    .suggestions-section {
      margin-top: 1rem;
      padding: 1rem;
      background-color: #d1e7dd;
      border-radius: 0.375rem;
      border-left: 4px solid #198754;
    }

    .suggestions-title {
      font-weight: 600;
      color: #0f5132;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .suggestion-item {
      padding: 0.5rem;
      margin-bottom: 0.5rem;
      background-color: white;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .suggestion-item:hover {
      background-color: #f8f9fa;
    }

    .suggestion-players {
      font-weight: 500;
      color: #212529;
    }

    .suggestion-reason {
      font-size: 0.85rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    @media (max-width: 768px) {
      .col-md-6 {
        width: 100%;
      }

      .modal-dialog {
        margin: 0.5rem;
      }
    }
  `;

  // SVG Icons
  private svgUsers() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="currentColor"><path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0H21.3C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/></svg>`;
  }

  private svgWarning() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/></svg>`;
  }

  private svgInfo() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>`;
  }

  private svgLightbulb() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M272 384c9.6-31.9 29.5-59.1 49.2-86.2l0 0c5.2-7.1 10.4-14.2 15.4-21.4c19.8-28.5 31.4-63 31.4-100.3C368 78.8 289.2 0 192 0S16 78.8 16 176c0 37.3 11.6 71.9 31.4 100.3c5 7.2 10.2 14.3 15.4 21.4l0 0c19.8 27.1 39.7 54.4 49.2 86.2H272zM192 512c44.2 0 80-35.8 80-80V416H112v16c0 44.2 35.8 80 80 80zM112 176c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-61.9 50.1-112 112-112c8.8 0 16 7.2 16 16s-7.2 16-16 16c-44.2 0-80 35.8-80 80z"/></svg>`;
  }

  private svgSave() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V173.3c0-17-6.7-33.3-18.7-45.3L352 50.7C340 38.7 323.7 32 306.7 32H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V128zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`;
  }

  private svgSpinner() {
    return html`<svg class="icon spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M304 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1 48 48 0 1 0 142.9 437zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437A48 48 0 1 0 437 369.1 48 48 0 1 0 369.1 437z"/></svg>`;
  }

  /**
   * Metodo pubblico per aprire il modale
   */
  async open(matchId: number, currentPlayers: CurrentPlayers, roundNumber?: number) {
    this.currentMatchId = matchId;
    this.currentRoundNumber = roundNumber || null;
    this.isOpen = true;
    this.errorMessage = '';
    this.isLoading = false;
    this.validationWarnings = [];
    this.playerSuggestions = [];
    
    // Imposta i giocatori selezionati (possono essere vuoti)
    this.selectedPlayers = {
      side1_player1: currentPlayers.side1_player1_id || '',
      side1_player2: currentPlayers.side1_player2_id || '',
      side2_player1: currentPlayers.side2_player1_id || '',
      side2_player2: currentPlayers.side2_player2_id || ''
    };

    // Carica sempre i giocatori disponibili per la giornata
    await this.loadAvailablePlayers();
    
    // Valida se ci sono già giocatori selezionati
    if (this.hasSelectedPlayers()) {
      await this.validateSelection();
    }
  }

  /**
   * Metodo pubblico per chiudere il modale
   */
  close() {
    this.isOpen = false;
    this.currentMatchId = null;
    this.currentRoundNumber = null;
    this.errorMessage = '';
    this.isSaving = false;
    this.selectedPlayers = {
      side1_player1: '',
      side1_player2: '',
      side2_player1: '',
      side2_player2: ''
    };
    
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  private handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  private handlePlayerSelect(team: string, position: string, e: Event) {
    const select = e.target as HTMLSelectElement;
    const key = `${team}_${position}` as keyof typeof this.selectedPlayers;
    
    this.selectedPlayers = {
      ...this.selectedPlayers,
      [key]: select.value
    };

    // Validazione real-time
    this.validateSelection();

    this.requestUpdate();
  }

  private hasSelectedPlayers(): boolean {
    const { side1_player1, side1_player2, side2_player1, side2_player2 } = this.selectedPlayers;
    return !!(side1_player1 || side1_player2 || side2_player1 || side2_player2);
  }

  private validateTeamSelection(): { valid: boolean; message?: string } {
    const { side1_player1, side1_player2, side2_player1, side2_player2 } = this.selectedPlayers;
    const selectedPlayers = [side1_player1, side1_player2, side2_player1, side2_player2].filter(id => id !== '');

    // Verifica che tutti i giocatori siano selezionati
    if (selectedPlayers.length !== 4) {
      return { valid: false, message: 'Devi selezionare tutti e 4 i giocatori.' };
    }

    // Verifica che non ci siano duplicati
    const uniquePlayers = new Set(selectedPlayers);
    if (uniquePlayers.size !== 4) {
      return { valid: false, message: 'Non puoi selezionare lo stesso giocatore più volte.' };
    }

    // Verifica che i giocatori nella stessa coppia siano diversi
    if (side1_player1 === side1_player2) {
      return { valid: false, message: 'I due giocatori della Coppia 1 devono essere diversi.' };
    }
    if (side2_player1 === side2_player2) {
      return { valid: false, message: 'I due giocatori della Coppia 2 devono essere diversi.' };
    }

    return { valid: true };
  }

  /**
   * Valida la selezione tramite API
   */
  private async validateSelection() {
    if (!this.hasSelectedPlayers()) {
      this.validationWarnings = [];
      this.playerSuggestions = [];
      return;
    }

    const { side1_player1, side1_player2, side2_player1, side2_player2 } = this.selectedPlayers;

    try {
      const response = await fetch('/simulations/api/manual-round-robin/validate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCSRFToken()
        },
        body: JSON.stringify({
          tournament_id: this.tournamentId,
          match_id: this.currentMatchId,
          round_number: this.currentRoundNumber,
          side1_player1_id: side1_player1 ? parseInt(side1_player1) : null,
          side1_player2_id: side1_player2 ? parseInt(side1_player2) : null,
          side2_player1_id: side2_player1 ? parseInt(side2_player1) : null,
          side2_player2_id: side2_player2 ? parseInt(side2_player2) : null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.validationWarnings = data.warnings || [];
        this.playerSuggestions = data.suggestions || [];
        this.availablePlayerIds = data.available_player_ids || [];
      }
    } catch (error) {
      console.error('Errore validazione:', error);
    }
  }

  /**
   * Carica i giocatori disponibili per la giornata corrente
   */
  private async loadAvailablePlayers() {
    if (!this.currentRoundNumber) {
      // Se non c'è round number, tutti i giocatori sono disponibili
      this.availablePlayerIds = this.allPlayers.map(p => p.id);
      return;
    }

    try {
      const response = await fetch('/simulations/api/manual-round-robin/validate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCSRFToken()
        },
        body: JSON.stringify({
          tournament_id: this.tournamentId,
          match_id: this.currentMatchId,
          round_number: this.currentRoundNumber,
          side1_player1_id: null,
          side1_player2_id: null,
          side2_player1_id: null,
          side2_player2_id: null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.availablePlayerIds = data.available_player_ids || [];
      }
    } catch (error) {
      console.error('Errore caricamento giocatori disponibili:', error);
      // Fallback: tutti i giocatori disponibili
      this.availablePlayerIds = this.allPlayers.map(p => p.id);
    }
  }

  private getCSRFToken(): string {
    const cookie = document.cookie.split('; ').find(row => row.startsWith('csrftoken='));
    return cookie ? cookie.split('=')[1] : '';
  }

  private findPlayerName(playerId: string): string | null {
    const player = this.allPlayers.find(p => p.id.toString() === playerId);
    return player ? player.name : null;
  }

  /**
   * Filtra i giocatori disponibili e già selezionati nel match corrente
   */
  private getSelectablePlayers(): Player[] {
    // Se non abbiamo ancora caricato i disponibili, mostra tutti
    if (this.availablePlayerIds.length === 0) {
      return this.allPlayers;
    }

    // Giocatori attualmente selezionati nel match
    const currentlySelected = Object.values(this.selectedPlayers)
      .filter(id => id !== '')
      .map(id => parseInt(id));

    // Mostra solo giocatori disponibili + quelli già selezionati nel match corrente
    return this.allPlayers.filter(player => 
      this.availablePlayerIds.includes(player.id) || 
      currentlySelected.includes(player.id)
    );
  }

  /**
   * Verifica se un giocatore è disponibile (non impegnato nella giornata)
   */
  private isPlayerAvailable(playerId: number): boolean {
    // Se non abbiamo ancora caricato i disponibili, considera tutti disponibili
    if (this.availablePlayerIds.length === 0) {
      return true;
    }
    return this.availablePlayerIds.includes(playerId);
  }

  /**
   * Filtra i warning per una specifica coppia
   */
  private getSideWarnings(side: 'side1' | 'side2'): ValidationWarning[] {
    return this.validationWarnings.filter(warning => {
      // Warning "already_faced" è generale, non va mostrato per coppia specifica
      if (warning.type === 'already_faced') {
        return false;
      }
      
      // Warning "already_in_round" - verifica se il player_id appartiene alla coppia
      if (warning.type === 'already_in_round' && warning.player_id) {
        const playerId = warning.player_id.toString();
        if (side === 'side1') {
          return playerId === this.selectedPlayers.side1_player1 || 
                 playerId === this.selectedPlayers.side1_player2;
        } else {
          return playerId === this.selectedPlayers.side2_player1 || 
                 playerId === this.selectedPlayers.side2_player2;
        }
      }
      
      // Warning "already_teammates" - usa il lato indicato, se disponibile.
      if (warning.type === 'already_teammates') {
        // Se il backend ha specificato il lato, usa quello.
        if (warning.side) {
          return warning.side === side;
        }
        
        // Fallback: verifica analizzando i nomi dei giocatori nel messaggio
        const t1p1_name = this.findPlayerName(this.selectedPlayers.side1_player1);
        const t1p2_name = this.findPlayerName(this.selectedPlayers.side1_player2);
        const t2p1_name = this.findPlayerName(this.selectedPlayers.side2_player1);
        const t2p2_name = this.findPlayerName(this.selectedPlayers.side2_player2);
        
        const msg = warning.message;
        
        if (side === 'side1') {
          // Verifica se il warning riguarda i giocatori del side1
          return (t1p1_name && t1p2_name && msg.includes(t1p1_name) && msg.includes(t1p2_name));
        } else {
          // Verifica se il warning riguarda i giocatori del side2
          return (t2p1_name && t2p2_name && msg.includes(t2p1_name) && msg.includes(t2p2_name));
        }
      }
      
      return false;
    });
  }

  /**
   * Ottieni i warning generali (già affrontati)
   */
  private getGeneralWarnings(): ValidationWarning[] {
    return this.validationWarnings.filter(warning => 
      warning.type === 'already_faced'
    );
  }

  /**
   * Applica un suggerimento selezionato dall'utente
   */
  private applySuggestion(suggestion: PlayerSuggestion) {
    this.selectedPlayers = {
      ...this.selectedPlayers,
      side2_player1: suggestion.player1_id.toString(),
      side2_player2: suggestion.player2_id.toString()
    };

    this.playerSuggestions = [];
    this.validateSelection();
    this.requestUpdate();
  }

  private async handleSave() {
    // Validazione
    const validation = this.validateTeamSelection();
    if (!validation.valid) {
      this.errorMessage = validation.message || '';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const response = await fetch('/simulations/api/manual-round-robin/assign-players/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCSRFToken()
        },
        body: JSON.stringify({
          tournament_id: this.tournamentId,
          match_id: this.currentMatchId,
          side1_player1_id: parseInt(this.selectedPlayers.side1_player1),
          side1_player2_id: parseInt(this.selectedPlayers.side1_player2),
          side2_player1_id: parseInt(this.selectedPlayers.side2_player1),
          side2_player2_id: parseInt(this.selectedPlayers.side2_player2),
        })
      });

      if (response.ok) {
        this.dispatchEvent(new CustomEvent('players-assigned', {
          bubbles: true,
          composed: true,
          detail: { matchId: this.currentMatchId }
        }));
        this.close();
      } else {
        const error = await response.json();
        this.errorMessage = error.message || 'Errore durante il salvataggio.';
      }
    } catch (error) {
      console.error('Errore salvataggio:', error);
      this.errorMessage = 'Errore di rete. Riprova.';
    } finally {
      this.isSaving = false;
    }
  }

  render() {
    return html`
      <!-- Backdrop -->
      <div 
        class="modal-backdrop ${this.isOpen ? 'show' : ''}"
        @click="${this.handleBackdropClick}"
      ></div>

      <!-- Modal -->
      <div 
        class="modal ${this.isOpen ? 'show' : ''}"
        @click="${this.handleBackdropClick}"
      >
        <div class="modal-dialog">
          <div class="modal-content" @click="${(e: Event) => e.stopPropagation()}">
            
            <!-- Header -->
            <div class="modal-header">
              <h5 class="modal-title">
                ${this.svgUsers()}
                Assegna Giocatori al Match
              </h5>
              <button 
                type="button" 
                class="btn-close" 
                @click="${this.close}"
                ?disabled="${this.isSaving}"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body">
              
              <!-- Alert informativo -->
              <div class="alert alert-info">
                <strong>${this.svgInfo()} Come funziona:</strong>
                <ul class="mb-0 mt-2">
                  <li>Seleziona 4 giocatori diversi per popolare il match</li>
                  <li>Il sistema ti avviserà se un giocatore gioca già nella stessa giornata</li>
                  <li>Riceverai suggerimenti per evitare che gli stessi giocatori si affrontino più volte</li>
                </ul>
              </div>

              ${this.errorMessage ? html`
                <div class="alert alert-danger">
                  <strong>${this.svgWarning()} Errore:</strong> ${this.errorMessage}
                </div>
              ` : ''}

              <!-- Coppia 1 -->
              <div class="card">
                <div class="card-header">Coppia 1</div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Giocatore 1</label>
                      <select 
                        class="form-select"
                        @change="${(e: Event) => this.handlePlayerSelect('side1', 'player1', e)}"
                        .value="${this.selectedPlayers.side1_player1}"
                        ?disabled="${this.isSaving}"
                      >
                        <option value="">Seleziona giocatore...</option>
                        ${this.getSelectablePlayers().map(player => html`
                          <option 
                            value="${player.id}"
                            ?selected="${this.selectedPlayers.side1_player1 === player.id.toString()}"
                          >
                            ${player.name} (${player.skill})
                          </option>
                        `)}
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Giocatore 2</label>
                      <select 
                        class="form-select"
                        @change="${(e: Event) => this.handlePlayerSelect('side1', 'player2', e)}"
                        .value="${this.selectedPlayers.side1_player2}"
                        ?disabled="${this.isSaving}"
                      >
                        <option value="">Seleziona giocatore...</option>
                        ${this.getSelectablePlayers().map(player => html`
                          <option 
                            value="${player.id}"
                            ?selected="${this.selectedPlayers.side1_player2 === player.id.toString()}"
                          >
                            ${player.name} (${player.skill})
                          </option>
                        `)}
                      </select>
                    </div>
                  </div>

                  <!-- Warnings Coppia 1 -->
                  ${this.getSideWarnings('side1').length > 0 ? html`
                    <div class="validation-warnings">
                      ${this.getSideWarnings('side1').map(warning => html`
                        <div class="warning-item ${warning.type} ${warning.severity || ''}">
                          <span class="warning-icon">${warning.icon || '⚠️'}</span>
                          <span>${warning.message}</span>
                        </div>
                      `)}
                    </div>
                  ` : ''}
                </div>
              </div>

              <!-- Coppia 2 -->
              <div class="card">
                <div class="card-header">Coppia 2</div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Giocatore 1</label>
                      <select 
                        class="form-select"
                        @change="${(e: Event) => this.handlePlayerSelect('side2', 'player1', e)}"
                        .value="${this.selectedPlayers.side2_player1}"
                        ?disabled="${this.isSaving}"
                      >
                        <option value="">Seleziona giocatore...</option>
                        ${this.getSelectablePlayers().map(player => html`
                          <option 
                            value="${player.id}"
                            ?selected="${this.selectedPlayers.side2_player1 === player.id.toString()}"
                          >
                            ${player.name} (${player.skill})
                          </option>
                        `)}
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Giocatore 2</label>
                      <select 
                        class="form-select"
                        @change="${(e: Event) => this.handlePlayerSelect('side2', 'player2', e)}"
                        .value="${this.selectedPlayers.side2_player2}"
                        ?disabled="${this.isSaving}"
                      >
                        <option value="">Seleziona giocatore...</option>
                        ${this.getSelectablePlayers().map(player => html`
                          <option 
                            value="${player.id}"
                            ?selected="${this.selectedPlayers.side2_player2 === player.id.toString()}"
                          >
                            ${player.name} (${player.skill})
                          </option>
                        `)}
                      </select>
                    </div>
                  </div>

                  <!-- Warnings Coppia 2 -->
                  ${this.getSideWarnings('side2').length > 0 ? html`
                    <div class="validation-warnings">
                      ${this.getSideWarnings('side2').map(warning => html`
                        <div class="warning-item ${warning.type} ${warning.severity || ''}">
                          <span class="warning-icon">${warning.icon || '⚠️'}</span>
                          <span>${warning.message}</span>
                        </div>
                      `)}
                    </div>
                  ` : ''}
                </div>
              </div>

              <!-- Warning Generali (già affrontati, etc) -->
              ${this.getGeneralWarnings().length > 0 ? html`
                <div class="validation-warnings">
                  ${this.getGeneralWarnings().map(warning => html`
                    <div class="warning-item ${warning.type} ${warning.severity || ''}">
                      <span class="warning-icon">${warning.icon || 'ℹ️'}</span>
                      <span>${warning.message}</span>
                    </div>
                  `)}
                </div>
              ` : ''}

              <!-- Suggerimenti -->
              ${this.playerSuggestions.length > 0 ? html`
                <div class="suggestions-section">
                  <div class="suggestions-title">
                    ${this.svgLightbulb()}
                    Suggerimenti per Coppia 2
                  </div>
                  ${this.playerSuggestions.map(suggestion => html`
                    <div 
                      class="suggestion-item"
                      @click="${() => this.applySuggestion(suggestion)}"
                    >
                      <div class="suggestion-players">
                        ${suggestion.player1_name} & ${suggestion.player2_name}
                      </div>
                      <div class="suggestion-reason">
                        ${suggestion.reason}
                      </div>
                    </div>
                  `)}
                </div>
              ` : ''}

            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <button 
                type="button" 
                class="btn btn-secondary"
                @click="${this.close}"
                ?disabled="${this.isSaving}"
              >
                Annulla
              </button>
              <button 
                type="button" 
                class="btn btn-primary"
                @click="${this.handleSave}"
                ?disabled="${this.isSaving}"
              >
                ${this.isSaving ? html`${this.svgSpinner()} Salvataggio...` : html`${this.svgSave()} Salva`}
              </button>
            </div>

          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-assign-players-modal': AssignPlayersModal;
  }
}
